import React, { useState, useEffect } from 'react';
import type { ToastProps, ToastPosition, ToastColor } from './Toast.types';

const positionClasses: Record<ToastPosition, string> = {
  'top-left': 'top-4 left-4',
  'top-center': 'top-4 left-1/2 -translate-x-1/2',
  'top-right': 'top-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
  'bottom-right': 'bottom-4 right-4',
};

const animationClasses: Record<ToastPosition, { in: string; out: string }> = {
  'top-left': { in: 'translate-y-0 opacity-100', out: '-translate-y-8 opacity-0' },
  'top-center': { in: '-translate-x-1/2 translate-y-0 opacity-100', out: '-translate-x-1/2 -translate-y-8 opacity-0' },
  'top-right': { in: 'translate-y-0 opacity-100', out: '-translate-y-8 opacity-0' },
  'bottom-left': { in: 'translate-y-0 opacity-100', out: 'translate-y-8 opacity-0' },
  'bottom-center': { in: '-translate-x-1/2 translate-y-0 opacity-100', out: '-translate-x-1/2 translate-y-8 opacity-0' },
  'bottom-right': { in: 'translate-y-0 opacity-100', out: 'translate-y-8 opacity-0' },
};

const containerColorClasses: Record<ToastColor, string> = {
  'default': 'text-gray-500 bg-white border border--gray-100 dark:text-gray-400 dark:bg-gray-950 dark:border-gray-950',
  'zinc': 'text-zinc-700 bg-zinc-100 border border-zinc-100 dark:text-zinc-400 dark:bg-zinc-950 dark:border-zinc-950',
  'red': 'text-red-700 bg-red-100 border border-red-100 dark:text-red-400 dark:bg-red-950 dark:border-red-950',
  'orange': 'text-orange-700 bg-orange-100 border border-orange-100 dark:text-orange-400 dark:bg-orange-950 dark:border-orange-950',
  'amber': 'text-amber-700 bg-amber-100 border border-amber-100 dark:text-amber-400 dark:bg-amber-950 dark:border-amber-950',
  'yellow': 'text-yellow-700 bg-yellow-100 border border-yellow-100 dark:text-yellow-400 dark:bg-yellow-950 dark:border-yellow-950',
  'lime': 'text-lime-700 bg-lime-100 border border-lime-100 dark:text-lime-400 dark:bg-lime-950 dark:border-lime-950',
  'green': 'text-green-700 bg-green-100 border border-green-100 dark:text-green-400 dark:bg-green-950 dark:border-green-950',
  'emerald': 'text-emerald-700 bg-emerald-100 border border-emerald-100 dark:text-emerald-400 dark:bg-emerald-950 dark:border-emerald-950',
  'teal': 'text-teal-700 bg-teal-100 border border-teal-100 dark:text-teal-400 dark:bg-teal-950 dark:border-teal-950',
  'cyan': 'text-cyan-700 bg-cyan-100 border border-cyan-100 dark:text-cyan-400 dark:bg-cyan-950 dark:border-cyan-950',
  'sky': 'text-sky-700 bg-sky-100 border border-sky-100 dark:text-sky-400 dark:bg-sky-950 dark:border-sky-950',
  'blue': 'text-blue-700 bg-blue-100 border border-blue-100 dark:text-blue-400 dark:bg-blue-950 dark:border-blue-950',
  'indigo': 'text-indigo-700 bg-indigo-100 border border-indigo-100 dark:text-indigo-400 dark:bg-indigo-950 dark:border-indigo-950',
  'violet': 'text-violet-700 bg-violet-100 border border-violet-100 dark:text-violet-400 dark:bg-violet-950 dark:border-violet-950',
  'purple': 'text-purple-700 bg-purple-100 border border-purple-100 dark:text-purple-400 dark:bg-purple-950 dark:border-purple-950',
  'fuchsia': 'text-fuchsia-700 bg-fuchsia-100 border border-fuchsia-100 dark:text-fuchsia-400 dark:bg-fuchsia-950 dark:border-fuchsia-950',
  'pink': 'text-pink-700 bg-pink-100 border border-pink-100 dark:text-pink-400 dark:bg-pink-950 dark:border-pink-950',
  'rose': 'text-rose-700 bg-rose-100 border border-rose-100 dark:text-rose-400 dark:bg-rose-950 dark:border-rose-950',
  'primary': 'text-primary-700 bg-primary-100 border border-primary-100 dark:text-primary-400 dark:bg-primary-950 dark:border-primary-950',
  'secondary': 'text-secondary-700 bg-secondary-100 border border-secondary-100 dark:text-secondary-400 dark:bg-secondary-950 dark:border-secondary-950',
  'tertiary': 'text-tertiary-700 bg-tertiary-100 border border-tertiary-100 dark:text-tertiary-400 dark:bg-tertiary-950 dark:border-tertiary-950',
};

export const Toast: React.FC<ToastProps> = ({
  position = 'bottom-right',
  color = 'default',
  icon: Icon,
  duration = 5000,
  autoClose = true,
  isVisible = false,
  onClose,
  title,
  message,
  className = '',
  closeLabel = 'Close',
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      // Trigger animation after render
      const animTimer = setTimeout(() => setIsAnimating(true), 10);

      let timer: NodeJS.Timeout;
      if (autoClose) {
        timer = setTimeout(() => {
          if (onClose) onClose(); // Envia señal de cierre al padre
        }, duration);
      }

      return () => {
        clearTimeout(animTimer);
        if (timer) clearTimeout(timer);
      };
    } else {
      setIsAnimating(false);
      // Retrasar la eliminación del DOM para permitir la animación de salida
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // 300ms coincide con duration-300 de Tailwind
      return () => clearTimeout(timer);
    }
  }, [isVisible, autoClose, duration, onClose]);

  const positionClass = positionClasses[position];
  const animationClass = isAnimating ? animationClasses[position].in : animationClasses[position].out;
  const containerColorClass = containerColorClasses[color];

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed flex items-center w-full max-w-xs p-4 gap-3 rounded-lg shadow-sm transition-all duration-300 ease-in-out ${positionClass} ${animationClass} ${containerColorClass} ${className}`}
      role="alert"
    >
      {Icon && (
        <div className="inline-flex items-center justify-center shrink-0 w-5 h-5 text-current">
          <Icon className="w-full h-full" aria-hidden="true" />
        </div>
      )}
      <div className="flex flex-col gap-1">
        {title && <div className="text-sm font-bold">{title}</div>}
        <div className="text-xs font-normal">{message}</div>
      </div>
      <button
        type="button"
        className="ms-auto -mx-1.5 -my-1.5 bg-transparent opacity-70 hover:opacity-100 rounded-lg focus:ring-2 focus:ring-current p-1.5 inline-flex items-center justify-center h-8 w-8 transition-opacity"
        data-dismiss-target="#toast-default"
        aria-label={closeLabel}
        onClick={onClose}
      >
        <span className="sr-only">{closeLabel}</span>
        <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
        </svg>
      </button>
    </div>
  );
};


